import { Injectable } from '@angular/core';
import { forkJoin, from } from 'rxjs';
import { ChatApiService } from './chat-api.service';
import { ConversationSummary, Project } from '../models/conversation.model';
import { Message } from '../models/chat.model';

/** Maps a raw API conversation message to the local Message model */
function mapApiMessage(m: any): Message {
	return {
		...m,
		id: m.messageId,
		mode: (m.mode as string)?.toLowerCase() as 'rag' | 'direct',
		sources: (m.sources ?? [])
			.filter((s: any) => s.s3FileVersionGUID)
			.map((s: any) => ({
				s3FileVersionGUID: s.s3FileVersionGUID,
				excerpt: s.snippet ?? s.excerpt ?? '',
			})),
	};
}

/** Maps a raw API conversation summary to the local model */
function mapApiConversation(c: any): ConversationSummary {
	return {
		id: c.conversationId,
		title: c.title,
		lastMessage: c.lastMessage,
		timestamp: c.timestamp,
		projectId: c.projectId,
		pinned: c.pinned,
		mode: c.mode?.toLowerCase(),
	};
}

/** Maps a raw API project to the local model */
function mapApiProject(p: any): Project {
	return {
		id: p.projectId,
		name: p.name,
		color: p.color,
		createdAt: p.createdAt,
		updatedAt: p.updatedAt,
	};
}

/**
 * Manages conversation and project history state.
 * Centralizes API calls for loading, selecting, and mutating conversations/projects.
 */
@Injectable({ providedIn: 'root' })
export class ConversationStateService {
	conversations: ConversationSummary[] = [];
	projects: Project[] = [];

	constructor(private chatApi: ChatApiService) {}

	/** Load conversations and projects from the API. Skips if already loaded unless forced. */
	loadHistory(internalUserGUID: string, force: boolean): void {
		if (!force && this.conversations.length > 0) return;
		forkJoin({
			convos: from(this.chatApi.getConversations({ internalUserGUID })),
			projs: from(this.chatApi.getProjects({ internalUserGUID })),
		}).subscribe({
			next: ({ convos, projs }) => {
				this.conversations = (convos ?? []).map(mapApiConversation);
				this.projects = (projs ?? []).map(mapApiProject);
			},
		});
	}

	/** Fetch a single conversation's full message history and return mapped messages + metadata */
	async getConversationMessages(conversationId: string): Promise<{ messages: Message[]; conversationId: string; mode?: 'rag' | 'direct' } | null> {
		const conv = await this.chatApi.getConversation({ conversationId });
		if (!conv) return null;

		const messages = (conv.messages ?? []).map(mapApiMessage);
		const mode = (conv.mode ?? conv.messages?.[0]?.mode)?.toLowerCase() as 'rag' | 'direct' | undefined;
		return { messages, conversationId: conv.conversationId, mode };
	}

	/** Rename a conversation and reload history */
	async renameConversation(conversationId: string, title: string, internalUserGUID: string): Promise<void> {
		await this.chatApi.renameConversation({ conversationId, title });
		this.loadHistory(internalUserGUID, true);
	}

	/** Remove a conversation from its project and reload history */
	async removeConversationFromProject(conversationId: string, internalUserGUID: string): Promise<void> {
		await this.chatApi.assignConversationToProject({ conversationId, projectId: undefined });
		this.loadHistory(internalUserGUID, true);
	}

	/** Toggle pin on a conversation and reload history */
	async togglePinConversation(conversationId: string, currentlyPinned: boolean, internalUserGUID: string): Promise<void> {
		await this.chatApi.pinConversation({ conversationId, pinned: !currentlyPinned });
		this.loadHistory(internalUserGUID, true);
	}

	/** Delete a conversation and reload history */
	async deleteConversation(conversationId: string, internalUserGUID: string): Promise<void> {
		await this.chatApi.deleteConversation({ conversationId });
		this.loadHistory(internalUserGUID, true);
	}

	/** Assign a conversation to a project and reload history */
	async assignConversationToProject(conversationId: string, projectId: string | undefined, internalUserGUID: string): Promise<void> {
		await this.chatApi.assignConversationToProject({ conversationId, projectId });
		this.loadHistory(internalUserGUID, true);
	}
}
